import { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/outline"
import Reactions from "aldoo-ra/Reactions"
import FingerprintJS from "@fingerprintjs/fingerprintjs"
import PlatformAuth from "aldoo-ra/PlatformAuth"

const reactionsManager = Reactions.getInstance()

const ThumbUp = ({ active }) => (
  <HandThumbUpIcon className={`w-[36px] h-[36px] stroke-2 ${active ? "text-white" : "text-text hover:text-primary transition-colors"}`} />
)

const ThumbDown = ({ active }) => (
  <HandThumbDownIcon className={`w-[36px] h-[36px] stroke-2 ${active ? "text-white" : "text-text hover:text-primary transition-colors"}`} />
)

const ArticleFeedback = ({ article, onFeedback }) => {
  const texts = TextBundle({ id: "help-center" })
  const [feedbackGiven, setFeedbackGiven] = useState(false)
  const [feedbackType, setFeedbackType] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [shouldShow, setShouldShow] = useState(false)
  const [isNewReaction, setIsNewReaction] = useState(false)
  const [auth] = PlatformAuth.useAuth()

  useEffect(() => {
    if (!article?._id) { setIsLoading(false); return }
    setFeedbackGiven(false)
    setFeedbackType(null)
    setIsNewReaction(false)
    setShouldShow(false)
    setError(null)
    checkExistingReaction()
  }, [article?._id])

  const getIdentifier = async () => {
    if (auth?._id) return auth._id
    if (!window.fpPromise) window.fpPromise = FingerprintJS.load()
    const fp = await window.fpPromise
    const result = await fp.get()
    return result.visitorId
  }

  const checkExistingReaction = async () => {
    try {
      setIsLoading(true)
      const identifier = await getIdentifier()
      const result = await reactionsManager.getStatus({
        contentId: article._id,
        contentType: "HCArticle",
        identifier,
        isAnonymous: !auth,
      })

      if (result.error) { setIsLoading(false); return }

      let hasReacted = false
      if (result.reaction && Array.isArray(result.reaction) && result.reaction.length > 0) {
        if (result.reaction.includes("positive")) {
          setFeedbackType("positive")
          hasReacted = true
        } else if (result.reaction.includes("negative")) {
          setFeedbackType("negative")
          hasReacted = true
        }
      }

      if (hasReacted) setFeedbackGiven(true)
      if (!hasReacted || isNewReaction) setShouldShow(true)
      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
    }
  }

  const handleFeedback = async (type) => {
    if (feedbackGiven || isSubmitting) return

    setFeedbackType(type)
    setFeedbackGiven(true)
    setIsSubmitting(true)
    setError(null)
    setIsNewReaction(true)

    try {
      const identifier = await getIdentifier()
      const response = await reactionsManager.toggleReaction({
        contentId: article._id,
        contentType: "HCArticle",
        identifier,
        isAnonymous: !auth,
        reaction: type,
      })

      if (response.error) throw new Error(response.error)
      if (onFeedback) onFeedback(type)
    } catch (err) {
      console.error("Error submitting feedback:", err)
      setError(texts?.feedback_error)
      setFeedbackType(null)
      setFeedbackGiven(false)
      setIsNewReaction(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!shouldShow || isLoading) return null
  if (!article || !article._id) return null

  return (
    <div className="w-[343px] md:w-[680px] lg:w-[590px] xl:w-[730px] md:h-[123px] xl:h-[160px] paper_20 py-8 pl-10 md:pl-8 pr-10 rounded-[30px] mt-14 mx-auto lg:mx-0">
      <div className="flex items-center justify-between w-full h-full">
        {!feedbackGiven ? (
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="w-full h-full flex flex-col items-center md:items-start justify-center">
              <Typography variant="h3" className="text-text mt-0 mb-4">
                {texts?.feedback_question}
              </Typography>
              <Typography variant="body1_dim" className="my-0">
                {texts?.feedback_subtitle}
              </Typography>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => handleFeedback("positive")}
                disabled={isSubmitting}
                className="flex items-center justify-center w-[72px] h-[72px] rounded-[20px] paper_20 hover:bg-paper shadow-md transition-colors"
                aria-label={texts?.feedback_yes_aria}
              >
                <ThumbUp />
              </button>
              <button
                onClick={() => handleFeedback("negative")}
                disabled={isSubmitting}
                className="flex items-center justify-center w-[72px] h-[72px] rounded-[20px] paper_20 hover:bg-paper shadow-md transition-colors"
                aria-label={texts?.feedback_no_aria}
              >
                <ThumbDown />
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6">
            <Typography variant="h3" className="text-text my-0">
              {texts?.feedback_thanks}
            </Typography>
            <div className="flex gap-4">
              <button
                disabled
                className={`flex items-center justify-center w-[72px] h-[72px] rounded-[20px] shadow-md ${
                  feedbackType === "positive" ? "bg-primary" : "bg-paper"
                }`}
              >
                <ThumbUp active={feedbackType === "positive"} />
              </button>
              <button
                disabled
                className={`flex items-center justify-center w-[72px] h-[72px] rounded-[20px] shadow-md ${
                  feedbackType === "negative" ? "bg-primary" : "bg-paper"
                }`}
              >
                <ThumbDown active={feedbackType === "negative"} />
              </button>
            </div>
          </div>
        )}
      </div>
      {error && <Typography variant="error">{error}</Typography>}
    </div>
  )
}

export default ArticleFeedback
